import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { API, logOut } from "../utils/common";
import i18n from "../utils/Language";
import { normalizeLang } from "../utils/normalizeLang";

// Layout components
import { AppSidebar } from "./layout/app-sidebar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "./ui/breadcrumb";
import { Separator } from "./ui/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "./ui/sidebar";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { ChevronDown, Globe, ArrowLeft, ArrowRight, Keyboard, Bell } from "lucide-react";
import { BreadcrumbProvider, useBreadcrumb } from "../contexts/BreadcrumbContext";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { Kbd } from "./ui/kbd";
import { useGlobalHotkeys } from "../hooks/use-global-hotkeys";
import { KeyboardShortcutsDialog } from "./layout/keyboard-shortcuts-dialog";
import { getBreadcrumbs } from "./layout/breadcrumbs";

const languageObj = {
  en: "ENG",
  ja: "JP",
  vi: "VI",
};

// Inner component that uses the breadcrumb context
const LayoutContent = ({ children, user, onLogout }) => {
  const { t } = useTranslation("common");
  const history = useHistory();
  const location = useLocation();
  const [language, setLanguage] = useState(
    normalizeLang(i18n.language) || normalizeLang(localStorage.getItem("lang")) || "vi"
  );
  const [isKeyboardShortcutsOpen, setIsKeyboardShortcutsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const { getDynamicLabel } = useBreadcrumb();
  const permissionsReady = Array.isArray(user?.permissions) || Array.isArray(user?.permission);
  const userPermissions = React.useMemo(() => {
    if (!permissionsReady) return [];
    if (Array.isArray(user?.permissions)) return user.permissions;
    if (Array.isArray(user?.permission)) return user.permission;
    return [];
  }, [permissionsReady, user]);

  const canAccess = React.useCallback(
    (requiredPermissions = [], mode = "any") => {
      if (!permissionsReady) return true;
      if (!requiredPermissions || requiredPermissions.length === 0) return true;
      if (mode === "all") {
        return requiredPermissions.every((permission) => userPermissions.includes(permission));
      }
      return requiredPermissions.some((permission) => userPermissions.includes(permission));
    },
    [permissionsReady, userPermissions]
  );

  const canViewNotifications = permissionsReady ? canAccess(["notification.view"]) : false;

  const fetchUnreadCount = React.useCallback(async () => {
    if (!permissionsReady || !canViewNotifications) return;
    try {
      const res = await API.get("notifications/unread-count");
      setUnreadCount(res?.data?.data?.unread_count ?? 0);
    } catch (e) {
      // ignore
    }
  }, [permissionsReady, canViewNotifications]);

  const handleSetLang = (lang) => {
    const langNormalized = normalizeLang(lang) || "en";
    localStorage.setItem("lang", langNormalized);
    i18n.changeLanguage(langNormalized).catch(() => {});
    setLanguage(langNormalized);
  };

  useEffect(() => {
    const syncLanguage = (lng) => {
      const normalized =
        normalizeLang(lng) || normalizeLang(localStorage.getItem("lang")) || "vi";
      setLanguage(normalized);
    };

    syncLanguage(i18n.resolvedLanguage || i18n.language);

    i18n.on("languageChanged", syncLanguage);
    return () => {
      i18n.off("languageChanged", syncLanguage);
    };
  }, []);

  const breadcrumbs = getBreadcrumbs(location.pathname, t, getDynamicLabel);

  useGlobalHotkeys([
    {
      key: "?",
      handler: () => setIsKeyboardShortcutsOpen(true),
    },
    {
      key: "/",
      mod: true,
      handler: () => setIsKeyboardShortcutsOpen(true),
    },
  ]);

  useEffect(() => {
    if (!permissionsReady) return undefined;
    if (!canViewNotifications) {
      setUnreadCount(0);
      return undefined;
    }

    fetchUnreadCount();

    const onRefresh = () => fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    window.addEventListener("tna:notifications:refresh", onRefresh);

    return () => {
      clearInterval(interval);
      window.removeEventListener("tna:notifications:refresh", onRefresh);
    };
  }, [permissionsReady, canViewNotifications, fetchUnreadCount]);

  return (
    <SidebarProvider>
      <AppSidebar user={user} onLogout={onLogout} />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12 border-b">
          <div className="flex items-center gap-2 px-4 flex-1">
            <SidebarTrigger className="-ml-1" />
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => history.goBack()}
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="sr-only">Go back</span>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => history.goForward()}
            >
              <ArrowRight className="h-4 w-4" />
              <span className="sr-only">Go forward</span>
            </Button>
            <Separator
              orientation="vertical"
              className="mr-2 h-4"
            />
            <Breadcrumb>
              <BreadcrumbList>
                {breadcrumbs.map((crumb, index) => (
                  <React.Fragment key={index}>
                    {index > 0 && <BreadcrumbSeparator className="hidden md:block" />}
                    <BreadcrumbItem className={index === 0 ? "" : "hidden md:block"}>
                      {crumb.href ? (
                        <BreadcrumbLink href={crumb.href} onClick={(e) => {
                          e.preventDefault();
                          history.push(crumb.href);
                        }}>
                          {crumb.label}
                        </BreadcrumbLink>
                      ) : (
                        <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                      )}
                    </BreadcrumbItem>
                  </React.Fragment>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
          </div>

          <div className="px-4 flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-2"
                  onClick={() => setIsKeyboardShortcutsOpen(true)}
                >
                  <Keyboard className="h-4 w-4" />
                  <Kbd>?</Kbd>
                  <span className="sr-only">{t("label.Keyboard shortcuts")}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent data-slot="tooltip-content">
                <div className="flex items-center gap-2">
                  {t("label.Keyboard shortcuts")} <Kbd>?</Kbd>
                </div>
              </TooltipContent>
            </Tooltip>

            {canViewNotifications && (
              <Button
                variant="ghost"
                size="icon"
                className="relative h-8 w-8"
                onClick={() => history.push("/notifications")}
              >
                <Bell className="h-4 w-4" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium text-primary-foreground">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
                <span className="sr-only">{t("menu.notifications") || "Notifications"}</span>
              </Button>
            )}

            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1"
                >
                  <Globe className="h-4 w-4" />
                  {languageObj[language]}
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    handleSetLang("en");
                  }}
                >
                  English (ENG)
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    handleSetLang("ja");
                  }}
                >
                  Japanese (JP)
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    handleSetLang("vi");
                  }}
                >
                  Vietnamese (VI)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <KeyboardShortcutsDialog
          open={isKeyboardShortcutsOpen}
          onOpenChange={setIsKeyboardShortcutsOpen}
        />
        <div className="flex flex-1 flex-col gap-4 p-4 pt-2 overflow-x-hidden">
          {children}
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
};

// Main LayoutView component that provides the BreadcrumbProvider
const LayoutView = ({ children, user = {} }) => {
  const { t } = useTranslation("common");
  const history = useHistory();

  useEffect(() => {
    let unsubscribe = null;
    let isMounted = true;

    const swMessageHandler = (event) => {
      if (event?.data?.type === "PUSH_NOTIFICATION_RECEIVED") {
        window.dispatchEvent(new Event("tna:notifications:refresh"));
      }
    };

    const setupPushListeners = async () => {
      try {
        if (typeof Notification === "undefined" || Notification.permission !== "granted") return;

        const pushNotifications = await import("../utils/pushNotifications");
        if (!isMounted) return;

        await pushNotifications.initFirebaseMessaging();
        await pushNotifications.registerFcmTokenIfPermitted();
        unsubscribe = pushNotifications.listenForPushMessages(() => {
          window.dispatchEvent(new Event("tna:notifications:refresh"));
        });
        if (navigator?.serviceWorker) {
          navigator.serviceWorker.addEventListener("message", swMessageHandler);
        }
      } catch (e) {
        // ignore
      }
    };

    setupPushListeners();

    return () => {
      isMounted = false;
      if (unsubscribe) unsubscribe();
      if (navigator?.serviceWorker) {
        navigator.serviceWorker.removeEventListener("message", swMessageHandler);
      }
    };
  }, []);

  const handleLogout = () => {
    if (window.confirm(t("login.confirmout"))) {
      logOut();
      history.push("/login");
    }
  };

  return (
    <BreadcrumbProvider>
      <LayoutContent user={user} onLogout={handleLogout}>
        {children}
      </LayoutContent>
    </BreadcrumbProvider>
  );
};

export default LayoutView;
